import HeadMetadataLangAlternate from 'components/shared/HeadMetadataLangAlternate';
import { SlugsContext } from 'context/slugs/slugs.context';
import { useI18n } from 'hooks/useI18n';
import { usePageTrack } from 'hooks/usePageTrack';
import parse from 'html-react-parser';
import { Multilingual } from 'interfaces/offer.interface';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import Script from 'next/script';
import { useContext, useEffect } from 'react';
import WPService from 'services/wp.service';
import { APP_URLS } from 'utils/app-urls';
import { config } from 'utils/config';
import { avoidRateLimit } from 'utils/rate-limit';
import { WP_REST_API_Categories, WP_REST_API_Post } from 'wp-types';
import BlogPostComponent from '../../components/marketingSite/blog/BlogPostComponent';

export type WP_NextPrev = {
  id: number;
  slug: string;
  title: string;
};

type BlogPostPageProps = {
  disqusShortname?: string;
  post: WP_REST_API_Post & {
    next: WP_NextPrev | null;
    previous: WP_NextPrev | null;
  };
  categories: WP_REST_API_Categories;
};

const BlogPostPage: React.FC<BlogPostPageProps> = (props) => {
  const { t } = useI18n('blog');
  const yoastHead = parse(`${props.post.yoast_head}`);
  const { setSlug, clearSlug } = useContext(SlugsContext);

  usePageTrack('Blog - Page Loaded');

  useEffect(() => {
    setSlug(props.post.language_slugs as Multilingual);

    return () => {
      clearSlug();
    };
  }, [props.post.id]);

  return (
    <>
      <Head>{yoastHead}</Head>
      <Script
        id="blogMetadata"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "${t('pageTitle')}",
            "url": "https://www.getmanfred.com/blog",
            "description": "${t('pageDescription')}",
            "publisher": {
              "@type": "Organization",
              "name": "Manfred",
              "logo": {
                "@type": "ImageObject",
                "url": "https://media-asgard.s3.eu-west-1.amazonaws.com/manfred-media/logo_manfred_color.svg?v=38732000",
                "width": 200,
                "height": 50
              }
            }
          }`
        }}
      />
      <HeadMetadataLangAlternate
        url={APP_URLS.blog}
        path={{
          ES: `${props.post.slug}`,
          EN: `${props.post.slug}`
        }}
      />
      <BlogPostComponent {...props} />
    </>
  );
};

export default BlogPostPage;

export async function getStaticPaths() {
  await avoidRateLimit();
  const slugsES = await WPService.getSlugs('es');
  const slugsEN = await WPService.getSlugs('en');

  const pathsES = slugsES
    .slice(0, +config.pregenerateBlogPosts)
    .map((slug) => ({
      params: { slug },
      locale: 'es'
    }));

  const pathsEN = slugsEN
    .slice(0, +config.pregenerateBlogPosts)
    .map((slug) => ({
      params: { slug },
      locale: 'en'
    }));

  return {
    paths: [...pathsES, ...pathsEN],
    fallback: 'blocking'
  };
}

export async function getStaticProps({
  locale,
  params
}: {
  params: {
    slug: string;
  };
  locale: string;
}) {
  await avoidRateLimit();

  let post = await WPService.getPost(params.slug, locale);

  if (!post) {
    post = await WPService.getPost(params.slug, locale === 'en' ? 'es' : 'en');
  }

  if (!post) {
    return {
      revalidate: 10,
      redirect: {
        destination: `/${locale}/404`,
        permanent: false
      }
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'topbar',
        'blog',
        'footer'
      ])),
      post,
      categories: await WPService.getCategoriesInfo(post.categories),
      disqusShortname: config.disqusShortname
    },
    revalidate: 10
  };
}
